import React, { forwardRef } from 'react';
import MaterialTable, { Icons, MaterialTableProps } from '@material-table/core';
import { useTranslation } from 'react-i18next';
import { merge } from 'lodash';

import AddBox from '@material-ui/icons/AddBox';
import ArrowUpward from '@material-ui/icons/ArrowUpward';
import Check from '@material-ui/icons/Check';
import ChevronLeft from '@material-ui/icons/ChevronLeft';
import ChevronRight from '@material-ui/icons/ChevronRight';
import Clear from '@material-ui/icons/Clear';
import DeleteOutline from '@material-ui/icons/DeleteOutline';
import Edit from '@material-ui/icons/Edit';
import FilterList from '@material-ui/icons/FilterList';
import FirstPage from '@material-ui/icons/FirstPage';
import LastPage from '@material-ui/icons/LastPage';
import Remove from '@material-ui/icons/Remove';
import SaveAlt from '@material-ui/icons/SaveAlt';
import Search from '@material-ui/icons/Search';
import SettingsOutlinedIcon from '@material-ui/icons/SettingsOutlined';
import createStyles from '@material-ui/core/styles/createStyles';
import makeStyles from '@material-ui/core/styles/makeStyles';
import createMuiTheme, { Theme } from '@material-ui/core/styles/createTheme';
import { MuiThemeProvider } from '@material-ui/core/styles';

import { appTheme } from 'components/AppTheme';
import Info from 'components/Tooltip/Info';
import withLoading from 'components/Loading/withLoading';
import { EdwardColors } from 'enums';
import { borderRadiusBox } from 'components/AppTheme/index';

interface EnhancedProps {
  refresh?: boolean;
}

export const tableTheme: Theme = createMuiTheme(appTheme, {
  // overrides of default props
  props: {
    MuiTextField: {
      variant: 'standard',
      size: 'medium',
    },
    MuiButton: {
      variant: 'outlined',
    },
    MuiFormControl: {
      style: {
        marginTop: 0,
      },
    },
  },
  overrides: {
    MuiInput: {
      root: {
        paddingLeft: 7,
        border: `1px solid ${appTheme.palette.grey[200]}`,
        transition: 'all 250ms',

        '&.Mui-focused': {
          borderColor: appTheme.palette.grey[500],
        },
      },
      underline: {
        '&:before, &:after': {
          content: 'none',
        },
      },
      formControl: {
        marginTop: '0 !important',
      },
    },
    MuiFormControl: {
      root: {
        marginTop: 0,
        maxWidth: '500px',
      },
    },
    MuiFormControlLabel: {
      root: {
        display: 'none',
      },
    },
    MuiTableRow: {
      root: {
        '&:hover': {
          backgroundColor: appTheme.palette.grey['100'],
        },
      },
    },
    MuiTableCell: {
      root: {
        backgroundColor: 'transparent',
        padding: appTheme.spacing(1, 2),
      },
    },
    MuiTooltip: {
      tooltip: {
        backgroundColor: appTheme.palette.common.white,
        boxShadow: appTheme.shadows[2],
        color: appTheme.palette.text.primary,
        margin: appTheme.spacing(0.75),
        maxWidth: '40rem',
        padding: appTheme.spacing(1, 2),
        fontSize: appTheme.typography.body2.fontSize,
        fontWeight: appTheme.typography.body2.fontWeight,
      },
      tooltipPlacementTop: {
        margin: 0,
        '@media (min-width: 600px)': {
          margin: 0,
        },
      },
    },
    MuiToolbar: {
      gutters: {
        [appTheme.breakpoints.up('sm')]: {
          paddingLeft: 0,
        },
      },
    },
  },
});

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      maxWidth: '100%',
      '& th': { fontWeight: 700 },

      [theme.breakpoints.down('lg')]: {
        '& th, & td': {
          padding: '8px',
        },
        '& th:first-child, & td:first-child': {
          paddingLeft: '16px',
        },
        '& td': {
          paddingRight: '25px !important',
        },
        '& tfoot td': {
          paddingRight: '0 !important',
        },
      },
      [theme.breakpoints.down('sm')]: {
        '& th, & td': {
          padding: '4px',
          fontSize: '12px !important',
        },
        '& th:first-child, & td:first-child': {
          paddingLeft: '8px',
        },
      },
      '& > div > table': {
        marginTop: theme.spacing(1),
        '& tr:hover': {
          background: 'transparent',
        },

        '& td': {
          border: 'none',
          paddingLeft: '0px !important',
          '& > div': {
            minHeight: '40px',
            height: '40px',
            overflow: 'hidden',
            '& div[class^=MuiInputBase]': {
              border: `solid ${EdwardColors.GRAY_LIGHTER} 2px`,
              borderRadius: borderRadiusBox,
              height: '40px',
              '& input': {
                height: 0,
              },
            },
          },
        },
      },
      /*
      '&._extProd': {
        '& tbody tr:nth-last-child(2)': { display: 'none' },
      },
      */
      '&._extProdPdf': {
        overflowX: 'hidden',
        width: '710px',
        '& > div > div:first-child, & > div > div:first-child > div:not([class^="MuiToolbar-root"])': {
          display: 'block',
        },
        '& th, & td': { fontSize: '80% !important' },
        '& th': { padding: '4px 8px', lineHeight: '100%' },
        '& td': { height: '40px', padding: '4px 8px' },
        '& th:first-child, & td:first-child': { width: `0 !important`, padding: '0 !important' },
        '& td:first-child button': { display: 'none' },
      },
    },

    search: {
      '& div[class^=MuiToolbar-root]': {
        padding: theme.spacing(0, 0.5),
        '& > div:first-child': { display: 'none' },
        '& div[class^=MuiFormControl-root]': {
          maxWidth: '100%',
          paddingLeft: 0,
        },
        '& *:not([class*=MuiTableRow-footer]) div[class^=MuiInputBase-root]': {
          background: '#f5f5f5',
          height: theme.spacing(5),
          borderRadius: theme.spacing(1),
        },
      },
    },

    enhanced: (props: EnhancedProps) => ({
      '& > div': {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        [theme.breakpoints.down('sm')]: {
          justifyContent: 'end',
        },
      },
      '& > div > div:first-child': {
        // toolbar
        order: 1,
        //maxWidth: '50%',
        flexBasis: 'content',
        flexGrow: 1,
        display: 'flex',
        alignItems: 'center',

        [theme.breakpoints.down('sm')]: {
          flexBasis: '100%',
          margin: 0,
        },

        '& > div[class^=MuiToolbar-root]': {
          flexGrow: 1,
          paddingRight: 0,
          '& > div:first-child > div': {
            background: '#f5f5f5',
            height: theme.spacing(5),
            borderRadius: theme.spacing(1),
          },
          '& > div:nth-child(3)': {
            '& button': {
              padding: '2px',
              background: EdwardColors.LIGHT,
              borderRadius: borderRadiusBox,
              height: '40px',
              width: '40px',
              '& svg': { fontSize: '1.5rem', color: EdwardColors.GRAY_DARKER },
            },
          },
          [theme.breakpoints.down('sm')]: {
            width: '100%',
            flexWrap: 'wrap',
            padding: '0 10px 0 0',
            '& > div:first-child': { width: '100%', maxWidth: '100%', paddingLeft: '10px' },
            '& > div:nth-child(2)': { display: 'none' },
            '& > div:last-child': {
              margin: '10px 0 0 0px',
              width: props.refresh ? 'calc(10% + 4px)' : 'calc(12.5% + 4px)',
              textAlign: 'center',
              '& > div > div': { justifyContent: 'center' },
            },
          },
          [theme.breakpoints.down('xs')]: {
            '& > div:last-child': { margin: '10px', width: props.refresh ? 'calc(20% - 6px)' : 'calc(25% - 6px)' },
          },
        },
        '& > div:not([class^=MuiToolbar-root])': {
          // custom actions
          display: 'flex',

          [theme.breakpoints.down('sm')]: {
            margin: '-40px 0 0 10%',
            marginLeft: props.refresh ? '10%' : '12.5%',
            width: props.refresh ? 'calc(40% - 10px)' : 'calc(37.5% - 10px)',
            '& > div, & > button': {
              width: props.refresh ? '25%' : '33.333%',
              textAlign: 'center',
            },
          },
          [theme.breakpoints.down('xs')]: {
            margin: props.refresh ? '-60px 0 0 20%' : '-60px 0 0 25%',
            width: props.refresh ? 'calc(80% - 10px)' : 'calc(75% - 10px)',
            flexBasis: props.refresh ? 'calc(80% - 10px)' : 'calc(75% - 10px)',
            '& > div, & > button': {
              width: props.refresh ? '25%' : '33.333%',
            },
          },

          '& > div': {
            '& button': {
              borderRadius: borderRadiusBox,
              background: EdwardColors.LIGHT,
              marginLeft: theme.spacing(1),
              height: '40px',
              width: '40px',
              '& svg': { fontSize: '1.5rem', color: EdwardColors.GRAY_DARKER },
              [theme.breakpoints.down('sm')]: {
                marginLeft: 0,
              },
            },
          },
          '& > button': {
            // CSV
            height: theme.spacing(5),
            border: 'none',
            padding: '6px 10px 5px 10px',
            background: EdwardColors.LIGHT,
            margin: theme.spacing(0, 1),
            borderRadius: borderRadiusBox,
            '&:hover': {
              color: '#000',
            },
            '& svg': {
              fontSize: '1.5rem',
              color: EdwardColors.GRAY_DARKER,
            },
          },
        },
      },
      '& > div > table': {
        // paging
        marginTop: 0,
        order: 2,
        maxWidth: '50%',
        flexBasis: 'content',
        margin: theme.spacing(0, 1),

        [theme.breakpoints.down('sm')]: {
          margin: '-40px 10px 0 0',
          flexBasis: '50%',
          maxWidth: '50%',
        },

        [theme.breakpoints.down('xs')]: {
          marginTop: '0',
          marginLeft: '10px',
          width: '100%',
          maxWidth: '100%',
          flexBasis: '100%',
        },
        '& tr:hover': {
          background: 'transparent',
        },
        '& td': {
          border: 'none',
          paddingLeft: '0px !important',
          '& div[class^=MuiTablePagination-spacer]': {
            display: 'none',
          },
          '& > div': {
            display: 'flex',
            alignItems: 'stretch',
            justifyContent: 'space-evenly',
            minHeight: '40px',
            height: '40px',
            overflow: 'hidden',
            '& div[class^=MuiInputBase]': {
              border: `solid ${EdwardColors.GRAY_LIGHTER} 2px`,
              borderRadius: borderRadiusBox,
              height: '40px',
              '& input': {
                height: 0,
              },
            },
            '& div[class^=ForwardRef]': {
              minWidth: '50%',
              justifyContent: 'space-between',
              '& > span:first-child': { borderRight: 'solid #f5f5f5 2px' },
              '& > span:last-child': { borderLeft: 'solid #f5f5f5 2px' },
            },
            '& button': {
              padding: '6px',
            },
            '& span[class*=MuiTypography-caption]': {
              flexGrow: '0 !important' as unknown as number,
              fontSize: '16px',
              '@media (max-width:800px)': {
                fontSize: '14px',
              },
            },
          },
        },
      },
      '& > div > button': {
        order: 3,
        margin: theme.spacing(0, 1),
      },
      '& > div > div:nth-child(2)': {
        // table
        order: 4,
        width: '100%',
        marginTop: theme.spacing(1),
      },
    }),
    tooltip: {
      pointerEvents: 'all',
    },
  }),
);

const tableIcons: Icons = {
  Add: forwardRef((props, ref) => <AddBox {...props} ref={ref} />),
  Check: forwardRef((props, ref) => <Check {...props} ref={ref} />),
  Clear: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Delete: forwardRef((props, ref) => <DeleteOutline {...props} ref={ref} />),
  DetailPanel: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
  Edit: forwardRef((props, ref) => <Edit {...props} ref={ref} />),
  Export: forwardRef((props, ref) => <SaveAlt {...props} ref={ref} />),
  Filter: forwardRef((props, ref) => <FilterList {...props} ref={ref} />),
  FirstPage: forwardRef((props, ref) => <FirstPage {...props} ref={ref} />),
  LastPage: forwardRef((props, ref) => <LastPage {...props} ref={ref} />),
  NextPage: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
  PreviousPage: forwardRef((props, ref) => <ChevronLeft {...props} ref={ref} />),
  ResetSearch: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Search: forwardRef((props, ref) => <Search {...props} ref={ref} />),
  SortArrow: forwardRef((props, ref) => <ArrowUpward {...props} ref={ref} />),
  ThirdStateCheck: forwardRef((props, ref) => <Remove {...props} ref={ref} />),
  ViewColumn: forwardRef((props, ref) => <SettingsOutlinedIcon {...props} ref={ref} />),
};

interface TableProps extends MaterialTableProps<any> {
  title?: string;
  enhanced?: boolean;
  search?: boolean;
  className?: string;
  refresh?: boolean;

  quickfix?: boolean; // EDS-3816
}
const Table: React.FC<TableProps> = (props) => {
  const { t } = useTranslation();
  const classes = useStyles({ refresh: props.refresh });

  return (
    <MuiThemeProvider theme={tableTheme}>
      <div
        className={`${classes.root} ${props.enhanced ? classes.enhanced : ''} ${props.search ? classes.search : ''} ${
          props.className || ''
        }`}
      >
        <MaterialTable
          icons={tableIcons}
          localization={{
            pagination: {
              labelDisplayedRows: `{from}-{to} ${t('table_of')} {count}`,
              labelRowsSelect: t('table_rows'),
              labelRowsPerPage: t('table_rowsPerPage'),
              firstTooltip: props.quickfix ? '' : `${t('table_first')} ${t('table_page')}`,
              previousTooltip: props.quickfix ? '' : `${t('table_previous')} ${t('table_page')}`,
              nextTooltip: props.quickfix ? '' : `${t('table_next')} ${t('table_page')}`,
              lastTooltip: props.quickfix ? '' : `${t('table_last')} ${t('table_page')}`,
            },
            toolbar: {
              nRowsSelected: `{0} ${t('table_rows')} selected`,
              searchTooltip: t('table_search'),
              searchPlaceholder: t('table_search'),
              showColumnsTitle: t('table_addRemoveColumns'),
              addRemoveColumns: (
                <>
                  {t('table_addRemoveColumns')}
                  <Info className={classes.tooltip} content={t('table_columnsTooltip')} inline />
                </>
              ),
            },
            header: {
              actions: t('table_actions'),
            },
            body: {
              emptyDataSourceMessage: t('table_noRecords'),
              filterRow: {
                filterTooltip: t('table_filter'),
              },
            },
          }}
          title=""
          {...props}
          options={{ pageSizeOptions: [10, 20, 50], debounceInterval: 500, ...props.options }}
          components={merge({}, props.components, {
            Container: (containerProps: any) => <div style={containerProps.style}>{containerProps.children}</div>,
          })}
        />
      </div>
    </MuiThemeProvider>
  );
};

export default withLoading(Table);
